import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { CreateUserRequest, UpdateUserRequest } from '../types/auth';

// Create base API client
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and handle FormData
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Remove Content-Type header for FormData to let browser set multipart boundary
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If we get a 401 and haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const response = await axios.post(`${import.meta.env.VITE_API_URL || '/api/v1'}/auth/refresh`, {
            refreshToken,
          });

          const { token, refreshToken: newRefreshToken } = response.data;
          
          // Update stored tokens
          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', newRefreshToken);

          // Update the authorization header for the original request
          originalRequest.headers.Authorization = `Bearer ${token}`;

          // Retry the original request
          return apiClient(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          
          // Dispatch logout action if store is available
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

export { apiClient };

// Export specific API functions for common operations
export const authAPI = {
  login: (credentials: { username: string; password: string }) =>
    apiClient.post('/auth/login', credentials),
  
  logout: () =>
    apiClient.post('/auth/logout'),
  
  refreshToken: (refreshToken: string) =>
    apiClient.post('/auth/refresh', { refreshToken }),
  
  getCurrentUser: () =>
    apiClient.get('/auth/me'),
};

export const machinesAPI = {
  getAll: (params?: any) =>
    apiClient.get('/machines', { params }),
  
  getById: (id: string, includeRelations = false) =>
    apiClient.get(`/machines/${id}${includeRelations ? '?include_relations=true' : ''}`),
  
  create: (data: any) =>
    apiClient.post('/machines', data),
  
  update: (id: string, data: any) =>
    apiClient.put(`/machines/${id}`, data),
  
  delete: (id: string) =>
    apiClient.delete(`/machines/${id}`),
  
  uploadImage: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    return apiClient.post(`/machines/${id}/image`, formData);
  },
  
  getQRCode: (id: string) =>
    apiClient.get(`/machines/${id}/qr`),
  
  getStats: () =>
    apiClient.get('/machines/stats'),
};

export const manualsAPI = {
  getAll: (params?: any) =>
    apiClient.get('/manuals', { params }),
  
  getByMachineId: (machineId: string) =>
    apiClient.get(`/machines/${machineId}/manuals`),
  
  getById: (id: string) =>
    apiClient.get(`/manuals/${id}`),
  
  getForViewer: (id: string) =>
    apiClient.get(`/manuals/${id}/viewer`),
  
  uploadPDF: (machineId: string, title: string, file: File) => {
    const formData = new FormData();
    formData.append('pdf', file);
    formData.append('title', title);
    formData.append('type', 'pdf');
    return apiClient.post(`/machines/${machineId}/manuals/pdf`, formData);
  },
  
  createPhotoManual: (machineId: string, title: string) =>
    apiClient.post(`/machines/${machineId}/manuals/photo`, {
      title,
      type: 'photo',
    }),
  
  uploadPages: (manualId: string, files: File[]) => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('images', file);
    });
    return apiClient.post(`/manuals/${manualId}/pages/batch`, formData);
  },
  
  reorderPages: (manualId: string, pageIds: string[]) =>
    apiClient.post(`/manuals/${manualId}/pages/reorder`, {
      page_ids: pageIds,
    }),
  
  delete: (id: string) =>
    apiClient.delete(`/manuals/${id}`),
  
  getPDF: (id: string) =>
    apiClient.get(`/manuals/${id}/pdf`, { responseType: 'blob' }),
  
  getThumbnail: (id: string) =>
    apiClient.get(`/manuals/${id}/thumbnail`, { responseType: 'blob' }),
  
  getPageImage: (pageId: string, thumbnail = false) =>
    apiClient.get(`/pages/${pageId}/image${thumbnail ? '?thumbnail=true' : ''}`, {
      responseType: 'blob',
    }),
};

export const usersAPI = {
  getAll: (params?: any) =>
    apiClient.get('/users', { params }),
  
  getById: (id: string) =>
    apiClient.get(`/users/${id}`),
  
  create: (data: CreateUserRequest) =>
    apiClient.post('/users', data),
  
  update: (id: string, data: UpdateUserRequest) =>
    apiClient.put(`/users/${id}`, data),
  
  delete: (id: string) =>
    apiClient.delete(`/users/${id}`),
  
  changePassword: (id: string, data: { currentPassword: string; newPassword: string }) =>
    apiClient.put(`/users/${id}/password`, data),
};

export const inventoryAPI = {
  // Spare Parts
  spareParts: {
    getAll: (params?: any) =>
      apiClient.get('/spare-parts', { params }),
    
    getByMachineId: (machineId: string, params?: any) =>
      apiClient.get(`/machines/${machineId}/spare-parts`, { params }),
    
    create: (machineId: string, data: any) =>
      apiClient.post(`/machines/${machineId}/spare-parts`, data),
    
    update: (id: string, data: any) =>
      apiClient.put(`/spare-parts/${id}`, data),
    
    delete: (id: string) =>
      apiClient.delete(`/spare-parts/${id}`),
    
    getLowStock: (threshold?: number) =>
      apiClient.get('/spare-parts/low-stock', { params: { threshold } }),
    
    bulkUpdate: (updates: any[]) =>
      apiClient.post('/spare-parts/bulk-update', { updates }),
    
    updateStatus: (id: string, status: string, quantity?: number) =>
      apiClient.put(`/spare-parts/${id}/status`, { status, quantity }),
  },

  // Operating Supplies
  operatingSupplies: {
    getAll: (params?: any) =>
      apiClient.get('/operating-supplies', { params }),
    
    getByMachineId: (machineId: string, params?: any) =>
      apiClient.get(`/machines/${machineId}/operating-supplies`, { params }),
    
    create: (machineId: string, data: any) =>
      apiClient.post(`/machines/${machineId}/operating-supplies`, data),
    
    update: (id: string, data: any) =>
      apiClient.put(`/operating-supplies/${id}`, data),
    
    delete: (id: string) =>
      apiClient.delete(`/operating-supplies/${id}`),
    
    getLowStock: (threshold?: number) =>
      apiClient.get('/operating-supplies/low-stock', { params: { threshold } }),
    
    getExpiring: (days = 30) =>
      apiClient.get('/operating-supplies/expiring', { params: { days } }),
    
    bulkUpdate: (updates: any[]) =>
      apiClient.post('/operating-supplies/bulk-update', { updates }),
  },

  // Tools
  tools: {
    getAll: (params?: any) =>
      apiClient.get('/tools', { params }),
    
    getByMachineId: (machineId: string, params?: any) =>
      apiClient.get(`/machines/${machineId}/tools`, { params }),
    
    create: (machineId: string, data: any) =>
      apiClient.post(`/machines/${machineId}/tools`, data),
    
    update: (id: string, data: any) =>
      apiClient.put(`/tools/${id}`, data),
    
    delete: (id: string) =>
      apiClient.delete(`/tools/${id}`),
    
    getAvailable: (machineId?: string) =>
      apiClient.get('/tools/available', { params: { machine_id: machineId } }),
    
    getByStatus: (status: string) =>
      apiClient.get(`/tools/status/${status}`),
    
    getByLocation: (location: string) =>
      apiClient.get(`/tools/location/${location}`),
    
    updateStatus: (id: string, status: string) =>
      apiClient.put(`/tools/${id}/status`, { status }),
    
    updateLocation: (id: string, location: string) =>
      apiClient.put(`/tools/${id}/location`, { location }),
    
    bulkStatusUpdate: (updates: Array<{ id: string; status: string }>) =>
      apiClient.post('/tools/bulk-status-update', { updates }),
  },
};

export const notificationsAPI = {
  // Get active notifications (all authenticated users)
  getActive: () =>
    apiClient.get('/notifications/active'),

  // Admin-only endpoints
  getAll: (params?: { limit?: number; offset?: number }) =>
    apiClient.get('/notifications', { params }),

  getById: (id: string) =>
    apiClient.get(`/notifications/${id}`),

  create: (data: { message: string; ttl_hours: number }) =>
    apiClient.post('/notifications', data),

  archive: (id: string) =>
    apiClient.put(`/notifications/${id}/archive`),

  delete: (id: string) =>
    apiClient.delete(`/notifications/${id}`),
};